import { Link } from "react-router-dom";
import { WOMEN_SLUGS, MEN_SLUGS, pickCategories } from "@/data/homeCategories";

/**
 * Myntra-style mobile category rail — circular photo tiles pulled
 * directly from the admin-managed categories API. Two gendered rails
 * (Women / Men) so each has its own image set + accent color.
 *
 * Fallback if a category has no image in the DB: pink initial-letter
 * avatar (e.g. "H" for Handbags) so the rail never shows a broken
 * image.
 */

// Deterministic soft pastel background for the initial-letter avatar
// so each label gets its own recognisable tint but stays on-brand.
const AVATAR_TINTS = [
  "bg-pink/15 text-pink",
  "bg-pink/10 text-pink",
  "bg-ink/10 text-ink",
  "bg-amber-100 text-amber-700",
  "bg-emerald-100 text-emerald-700",
  "bg-indigo-100 text-indigo-700",
];

const tintFor = (label) => {
  let h = 0;
  for (let i = 0; i < label.length; i += 1) h = (h * 31 + label.charCodeAt(i)) | 0;
  return AVATAR_TINTS[Math.abs(h) % AVATAR_TINTS.length];
};

const Chip = ({ cat, testIdPrefix, ringClass }) => {
  const label = cat.name;
  const initial = (label || "?").trim().charAt(0).toUpperCase();
  return (
    <Link
      to={`/category/${cat.slug}`}
      className="flex flex-col items-center gap-1.5 flex-shrink-0 w-[72px]"
      data-testid={`chip-${testIdPrefix}-${cat.slug}`}
    >
      <span
        className={`relative w-[68px] h-[68px] rounded-full overflow-hidden border-2 ${ringClass} transition-all shadow-sm bg-white`}
      >
        {cat.image ? (
          <img
            src={cat.image}
            alt={label}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <span
            className={`absolute inset-0 flex items-center justify-center text-xl font-extrabold ${tintFor(label)}`}
          >
            {initial}
          </span>
        )}
      </span>
      <span className="text-[11px] font-semibold text-ink text-center leading-tight line-clamp-2">
        {label}
      </span>
    </Link>
  );
};

const ChipRow = ({ title, testIdPrefix, cats, ringClass, dotClass }) => {
  if (!cats.length) return null;
  return (
    <div>
      <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-ink-soft mb-3 px-4">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotClass}`} />
        {title}
      </p>
      <div
        className="flex gap-4 overflow-x-auto no-scrollbar px-4 pb-1"
        data-testid={`chip-row-${testIdPrefix}`}
      >
        {cats.map((c) => (
          <Chip key={`${testIdPrefix}-${c.slug}`} cat={c} testIdPrefix={testIdPrefix} ringClass={ringClass} />
        ))}
      </div>
    </div>
  );
};

const CategoryChipStrip = ({ categories = [] }) => {
  const women = pickCategories(categories, WOMEN_SLUGS);
  const men = pickCategories(categories, MEN_SLUGS);
  if (!women.length && !men.length) return null;
  return (
    <section
      className="md:hidden bg-white py-4 border-b border-line space-y-5"
      data-testid="mobile-category-chips"
    >
      <ChipRow
        title="Shop Women"
        testIdPrefix="women"
        cats={women}
        ringClass="border-pink/60 hover:border-pink"
        dotClass="bg-pink"
      />
      <ChipRow
        title="Shop Men"
        testIdPrefix="men"
        cats={men}
        ringClass="border-ink/25 hover:border-ink"
        dotClass="bg-ink"
      />
    </section>
  );
};

export default CategoryChipStrip;
